<template>
  <v-container>
    <!-- eslint-disable vue-router-rules/await-router-navigation -->
    <v-btn
      @click="$router.push({ name: 'Dashboard' })"
      class="focus-very-visible px-0 my-6"
      prepend-icon="west"
      variant="text"
    >
      <!-- eslint-enable vue-router-rules/await-router-navigation -->
      {{ $bct('Back to dashboard') }}
    </v-btn>

    <h1 class="d-flex justify-space-between mb-6">
      <span>{{ $bct('My forms') }}</span>

      <v-btn
        @click="newFamilyFormDialog.open()"
        color="primary"
        prepend-icon="mail_outline"
        size="large"
        variant="outlined"
        rounded
      >
        {{ $bct('Submit a form') }}
      </v-btn>
    </h1>

    <v-card
      class="my-6"
      color="transparent"
    >
      <v-autocomplete
        v-model="filters.schema_id"
        :item-title="(schema) => $bct(schema.name)"
        :items="familyFacingFormSchemas"
        :label="$bct('Filter by form type')"
        :width="$vuetify.display.mdAndUp ? '35%' : undefined"
        bg-color="white"
        color="primary"
        density="compact"
        item-value="id"
        variant="filled"
        clearable
      />
    </v-card>

    <DataTableServer
      @update:page="handlePageChange"
      :headers="tableHeaders"
      :items="forms"
      :items-length="totalItemCount"
      :items-per-page="PAGE_SIZE"
      :loading="processing"
      class="b-1 bc-very-light-grey rounded"
      item-key="id"
      no-data-text="No forms found"
    >
      <template #[`item.name`]="{ item }">
        <strong>{{ $bct(item.name) }}</strong>
        <p>
          <LongDateTime
            :date="item.submitted_at || item.created_at"
            :prefix="item.submitted_at ? 'Submitted' : 'Created'"
            class="fs-14"
          />
        </p>
      </template>

      <template #[`item.status`]="{ item }">
        {{ $bct(item.status) }}
      </template>

      <template #[`item.actions`]="{ item }">
        <v-btn
          @click="handleGoToForm(item)"
          :variant="item.submitted_at ? 'outlined' : undefined"
          class="my-2"
          color="primary"
        >
          {{ item.submitted_at ? $bct('View form') : $bct('Continue') }}
        </v-btn>
      </template>
    </DataTableServer>
  </v-container>

  <NewFamilyFormDialog
    ref="newFamilyFormDialog"
    back-to="FamilyFormIndex"
  />

  <FormReviewDialog ref="formReviewDialog" />
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import FormReviewDialog from '@/shared/components/form/FormReviewDialog.vue';
import LongDateTime from '@/shared/components/LongDateTime.vue';
import NewFamilyFormDialog from '@/parent/components/form/NewFamilyFormDialog.vue';
import useApi from '@/shared/composables/useApi';
import useRouterHelper from '@/shared/composables/useRouterHelper';

const $bct = inject('translator');
const PAGE_SIZE = 15;

const { api } = useApi();
const store = useStore();
const router = useRouter();
const { getFiltersFromQuery, updateQuery } = useRouterHelper();

const tableHeaders = [
  { title: 'Form name', value: 'name' },
  { title: 'Status', value: 'status' },
  { title: 'Details', value: 'actions' },
];

const filters = ref(getFiltersFromQuery({ paged: true }));
const forms = ref([]);
const formReviewDialog = ref(null);
const newFamilyFormDialog = ref(null);
const processing = ref(false);
const totalItemCount = ref(0);

const familyFacingFormSchemas = computed(() => store.state.familyFacingFormSchemas);

const stopWatchers = [
  watch(() => filters.value.schema_id, loadForms),
  watch(() => filters.value.page, loadForms),
];

onBeforeUnmount(() => {
  stopWatchers.forEach((stop) => stop());
});

async function handleGoToForm(form) {
  if (form.submitted_at) {
    formReviewDialog.value.open(form);
  } else {
    await router.push({
      name: 'FormContinue',
      params: {
        formId: form.id,
        schemaId: form.schema_id,
      },
      query: {
        backTo: 'FamilyFormIndex',
      },
    });
  }
}

function handlePageChange(newPage) {
  filters.value.page = newPage;
}

function load() {
  void loadForms();
}

async function loadForms() {
  processing.value = true;

  const params = {
    page_size: PAGE_SIZE,
    family_facing: true,
    ...filters.value,
  };
  await updateQuery({ ...filters.value });
  const response = await api.parent.form.index(params);
  totalItemCount.value = parseInt(response.headers['x-total-count'] || 0, 10);
  forms.value = response.data;

  processing.value = false;
}

onMounted(load);
</script>
